import React, { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { Button } from "@companion-core/shared/app/Components/Button";
import { ComponentData } from "@companion-core/shared/app/Interfaces/API";
import { PostListLayoutProps } from "@companion-core/shared/app/Interfaces/blocks";
import { getCloudinaryOptimizedUrl } from "@companion-core/shared/app/Utils/cloudinary";
import { getResolvedDescriptionAlt } from "@companion-core/shared/app/Utils/componentHelper";
import { sanitizeHtml } from "@companion-core/shared/app/Utils/htmlSanitizer";
import { useMediaQuery } from "@companion-core/web/src/Utilities/mediaQuery";
import { redirectTo } from "@companion-core/web/src/Utilities/navigation";
import {
  getSlideAccessibilityLabel,
  getSlideCtaLabel,
  getSlideImage,
  hasSlideImage,
  JOURNEY_SWIPE_THRESHOLD_PX,
} from "./postListJourneyLayout.utils";

import "@companion-core/web/src/Assets/Styles/scss/postListJourneyLayout.scss";
import { useMountEffect } from "@companion-core/shared/app/Hooks/useMountEffect";
import { trackPostListJourneyPage } from "@companion-core/shared/app/tracking/blocks";

const SWIPE_THRESHOLD_PX = JOURNEY_SWIPE_THRESHOLD_PX;

type JourneySlideProps = {
  slide: ComponentData;
  slideIndex: number;
  totalSlides: number;
};

const JourneySlide = ({ slide, slideIndex, totalSlides }: JourneySlideProps) => {
  const { t } = useTranslation();
  const slideImage = getSlideImage(slide);
  const withImage = hasSlideImage(slide);

  useMountEffect(() => {
    trackPostListJourneyPage({ slideIndex, totalSlides });
  });

  return (
    <article
      className={`post-list-journey-layout__slide ${
        withImage
          ? "post-list-journey-layout__slide--with-image"
          : "post-list-journey-layout__slide--without-image"
      }`}
      aria-label={getSlideAccessibilityLabel(slideIndex, totalSlides, t)}
      data-testid={`post-list-journey__slide--${slide.id}`}>
      {withImage && slideImage && (
        <img
          className="post-list-journey-layout__image"
          data-testid={`post-list-journey__media--${slide.id}`}
          src={getCloudinaryOptimizedUrl(slideImage.url, {
            contentType: "image",
            width: window.innerWidth,
          })}
          alt={getResolvedDescriptionAlt(slideImage.description_alt, slide.title)}
        />
      )}
      <div
        className={`post-list-journey-layout__text-content ${
          !withImage ? "post-list-journey-layout__text-content--centered" : ""
        }`}>
        {slide.title && (
          <h2
            className="post-list-journey-layout__title title-3"
            data-testid={`post-list-journey__title--${slide.id}`}
            // nosemgrep: typescript.react.security.audit.react-dangerouslysetinnerhtml.react-dangerouslysetinnerhtml
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(slide.title) }}
          />
        )}
        {slide.description && (
          <p
            className="post-list-journey-layout__subtitle body-text-6"
            data-testid={`post-list-journey__subtitle--${slide.id}`}
            // nosemgrep: typescript.react.security.audit.react-dangerouslysetinnerhtml.react-dangerouslysetinnerhtml
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(slide.description) }}
          />
        )}
      </div>
    </article>
  );
};

type JourneyPaginationProps = {
  totalSlides: number;
  activeIndex: number;
};

const JourneyPagination = ({ totalSlides, activeIndex }: JourneyPaginationProps) => (
  <div
    className="post-list-journey-layout__pagination"
    role="tablist"
    aria-label="Pagination"
    data-testid="post-list-journey__pagination">
    {Array.from({ length: totalSlides }).map((_, index) => (
      <span
        key={`journey-dot-${index}`}
        className={`post-list-journey-layout__pagination-dot ${
          activeIndex === index ? "post-list-journey-layout__pagination-dot--active" : ""
        }`}
        role="tab"
        aria-selected={activeIndex === index}
        data-testid={`post-list-journey__dot--${index}`}
      />
    ))}
  </div>
);

export const PostListJourneyLayout = ({ cards, lastSlideRedirection }: PostListLayoutProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobileSize = useMediaQuery("(max-width: 768px)");
  const [activeIndex, setActiveIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);

  const slides = useMemo(() => cards ?? [], [cards]);
  const totalSlides = slides.length;
  const isLastSlide = activeIndex >= totalSlides - 1;
  const currentSlide = slides[activeIndex];

  const goToNextSlide = useCallback(() => {
    if (!isLastSlide) {
      setActiveIndex((index) => index + 1);
    }
  }, [isLastSlide]);

  const handleCtaPress = useCallback(() => {
    if (!currentSlide) {
      return;
    }

    if (isLastSlide && lastSlideRedirection) {
      lastSlideRedirection({});
      return;
    }

    if (isLastSlide && "button_link" in currentSlide && currentSlide.button_link) {
      redirectTo(
        currentSlide.button_link,
        ("button_type" in currentSlide ? currentSlide.button_type : undefined) ?? "internal",
        navigate
      );
      return;
    }

    goToNextSlide();
  }, [currentSlide, goToNextSlide, isLastSlide, navigate]);

  const handleTouchStart = useCallback((event: React.TouchEvent<HTMLDivElement>) => {
    touchStartX.current = event.touches[0]?.clientX ?? null;
  }, []);

  const handleTouchEnd = useCallback(
    (event: React.TouchEvent<HTMLDivElement>) => {
      if (!isMobileSize || touchStartX.current === null || isLastSlide) {
        return;
      }

      const touchEndX = event.changedTouches[0]?.clientX ?? touchStartX.current;
      const deltaX = touchStartX.current - touchEndX;

      if (deltaX >= SWIPE_THRESHOLD_PX) {
        goToNextSlide();
      }

      touchStartX.current = null;
    },
    [goToNextSlide, isLastSlide, isMobileSize]
  );

  if (!slides.length || !currentSlide) {
    return null;
  }

  const ctaLabel = getSlideCtaLabel(currentSlide, isLastSlide, t);
  const currentSlideHasImage = hasSlideImage(currentSlide);

  return (
    <section
      className="post-list-journey-layout"
      data-testid="post-list-journey"
      aria-roledescription="carousel"
      aria-label={t("onboarding.journey.carousel-label", {
        defaultValue: "Introduction slides",
      })}>
      <p className="post-list-journey-layout__sr-only" aria-live="polite" aria-atomic="true">
        {getSlideAccessibilityLabel(activeIndex, totalSlides, t)}
      </p>
      <div
        className={`post-list-journey-layout__slides ${
          currentSlideHasImage ? "" : "post-list-journey-layout__slides--centered"
        }`}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}>
        <JourneySlide
          key={`post-list-journey__slide--${currentSlide.id}`}
          slide={currentSlide}
          slideIndex={activeIndex}
          totalSlides={totalSlides}
        />
        <JourneyPagination totalSlides={totalSlides} activeIndex={activeIndex} />
      </div>
      <footer className="post-list-journey-layout__footer" data-testid="post-list-journey__footer">
        <Button
          testID="post-list-journey__cta"
          label={ctaLabel}
          size="full"
          onClick={handleCtaPress}
          accessibilityLabel={ctaLabel}
        />
      </footer>
    </section>
  );
};
